import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, FileText, ExternalLink } from "lucide-react";
import { COMUNICAZIONI_SUB } from "../../mock";
import {
  adminListComunicazioni,
  adminCreateComunicazione,
  adminUpdateComunicazione,
  adminDeleteComunicazione,
  uploadFile,
  fileUrl,
} from "../../api";

const subLabel = (slug) => COMUNICAZIONI_SUB.find((s) => s.slug === slug)?.name || slug;
const empty = {
  id: null,
  subSlug: "avvisi-convocazione",
  title: "",
  company: "",
  date: "",
  summary: "",
  pdfUrl: "",
};

const ComunicazioniManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      setItems(await adminListComunicazioni());
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadFile(file);
      set("pdfUrl", url);
    } catch (err) {
      setMsg("Errore upload PDF");
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    const body = {
      subSlug: form.subSlug,
      title: form.title,
      company: form.company,
      date: form.date,
      summary: form.summary,
      pdfUrl: form.pdfUrl,
    };
    try {
      if (form.id) await adminUpdateComunicazione(form.id, body);
      else await adminCreateComunicazione(body);
      setForm(null);
      await load();
    } catch (err) {
      setMsg("Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare "${c.title}"?`)) return;
    await adminDeleteComunicazione(c.id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-sq-navy">Comunicazioni societarie</h2>
          <p className="text-sm text-gray-500">{items.length} comunicazioni pubblicate</p>
        </div>
        {!form && (
          <button
            onClick={() => setForm({ ...empty })}
            className="flex items-center gap-2 bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-4 py-2.5 rounded-sm text-sm font-medium"
          >
            <Plus size={16} /> Nuova comunicazione
          </button>
        )}
      </div>

      {msg && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{msg}</div>}

      {form && (
        <form onSubmit={save} className="bg-white border border-sq rounded-md p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-medium text-sq-navy">{form.id ? "Modifica comunicazione" : "Nuova comunicazione"}</h3>
            <button type="button" onClick={() => setForm(null)} className="text-gray-400 hover:text-sq-navy">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Sottocategoria</label>
              <select
                value={form.subSlug}
                onChange={(e) => set("subSlug", e.target.value)}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white"
              >
                {COMUNICAZIONI_SUB.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Società</label>
              <input
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                required
                placeholder="Es. Fincantieri S.p.A."
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-sq-navy mb-1">Titolo</label>
              <input
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                required
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Data (testo)</label>
              <input
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                placeholder="Es. 16 Giugno 2026 (vuoto = oggi)"
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Documento PDF</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 border border-sq rounded-sm px-3 py-2 text-sm cursor-pointer hover:border-sq-orange">
                  <Upload size={14} /> Carica PDF
                  <input type="file" accept="application/pdf" onChange={onUpload} className="hidden" />
                </label>
                {form.pdfUrl && (
                  <a href={fileUrl(form.pdfUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-sq-orange flex items-center gap-1">
                    <FileText size={13} /> PDF caricato
                  </a>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-sq-navy mb-1">Sommario</label>
              <textarea
                value={form.summary}
                onChange={(e) => set("summary", e.target.value)}
                rows={3}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={busy}
              className="bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Salvataggio..." : "Pubblica comunicazione"}
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="border border-sq text-sq-navy px-5 py-2.5 rounded-sm text-sm hover:bg-sq-light"
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-sq rounded-md overflow-hidden">
        <div className="divide-y divide-[#e6e8ec]">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-4 px-4 py-3 hover:bg-sq-light">
              <div className="w-9 h-9 rounded-sm bg-sq-light border border-sq flex items-center justify-center flex-shrink-0">
                <FileText size={16} className="text-sq-navy" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-sq-orange font-semibold truncate">{c.company}</span>
                  <span className="text-xs text-gray-400">· {subLabel(c.subSlug)}</span>
                </div>
                <p className="font-medium text-sq-navy truncate">{c.title}</p>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">{c.date}</span>
                  {c.pdfUrl && (
                    <a href={fileUrl(c.pdfUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-sq-orange flex items-center gap-1">
                      <ExternalLink size={11} /> PDF
                    </a>
                  )}
                </div>
              </div>
              <button onClick={() => setForm({ id: c.id, subSlug: c.subSlug, title: c.title, company: c.company, date: c.date || "", summary: c.summary || "", pdfUrl: c.pdfUrl || "" })} className="text-gray-400 hover:text-sq-orange p-2">
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(c)} className="text-gray-400 hover:text-red-600 p-2">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="px-4 py-8 text-center text-gray-500 text-sm">Nessuna comunicazione.</div>}
        </div>
      </div>
    </div>
  );
};

export default ComunicazioniManager;
